import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { BaseContext } from '../../../shared/base-context';
import { Id } from '../../../convex/_generated/dataModel';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../../catalyst-components/dialog';
import { Button } from '../../../catalyst-components/button';
import { Textarea } from '../../../catalyst-components/textarea';
import { AcornIcon } from '../../../shared/components/AcornIcon';
import { parseMarkdown } from '../../../shared/helpers/markdown';
import pig1 from '../../../assets/graphics/pig1.gif';

interface CheckinQuestion {
  id: string;
  type: "scale" | "text" | "choice";
  prompt: string;
  required: boolean;
  scale_min?: number;
  scale_max?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  choices?: string[];
}

interface CheckinData {
  _id: Id<"checkin_instructions">;
  greeting: string;
  questions?: CheckinQuestion[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  checkin: CheckinData;
  sessionsAtCheckin: number;
  checkinReward: number;
}

export const CheckinModal: React.FC<Props> = ({ open, onClose, checkin, sessionsAtCheckin, checkinReward }) => {
  const { t } = useTranslation();
  const { convexUserId } = useContext(BaseContext);
  const submitCheckinForm = useMutation(api.user_checkins.submitCheckinForm);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [earnedAcorns, setEarnedAcorns] = useState(0);

  const questions = checkin.questions || [];

  const updateAnswer = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const isFormValid = () => {
    return questions.every(q => {
      if (!q.required) return true;
      const answer = answers[q.id];
      return answer !== undefined && answer !== "";
    });
  };

  const handleSubmit = async () => {
    if (!convexUserId || !isFormValid()) return;

    setIsSubmitting(true);
    try {
      const formattedAnswers = questions
        .filter(q => answers[q.id] !== undefined && answers[q.id] !== "")
        .map(q => ({
          question_id: q.id,
          question_prompt: q.prompt,
          question_type: q.type,
          value: answers[q.id],
        }));

      const result = await submitCheckinForm({
        user_id: convexUserId as Id<"users">,
        checkin_id: checkin._id,
        sessions_at_checkin: sessionsAtCheckin,
        answers: formattedAnswers,
      });

      setEarnedAcorns(result.acorn_reward);
      setShowSuccess(true);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state after close animation
    setTimeout(() => {
      setAnswers({});
      setShowSuccess(false);
      setEarnedAcorns(0);
    }, 300);
    onClose();
  };

  if (showSuccess) {
    return (
      <Dialog open={open} onClose={handleClose} size="md">
        <DialogBody>
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-24 h-24 mb-4 animate-zoom-in">
              <img src={pig1} alt="" className="w-full h-full object-contain" />
            </div>
            <DialogTitle className="text-xl mb-2">
              {t('checkin.successTitle')}
            </DialogTitle>
            <div className="flex items-center gap-2 text-primary font-medium mb-4">
              <AcornIcon size={22} className="text-primary" />
              <span className="text-lg">
                {t('checkin.successMessage', { count: earnedAcorns })}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('checkin.successSubtext')}
            </p>
          </div>
          <DialogActions>
            <Button color="primary" className="rounded-full" onClick={handleClose}>
              {t('common.done')}
            </Button>
          </DialogActions>
        </DialogBody>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} size="xl">
      <DialogBody>
        {/* Header with greeting */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-20 h-20 mb-3">
            <img src={pig1} alt="" className="w-full h-full object-contain" />
          </div>
          <DialogTitle className="text-xl mb-2">
            {t('checkin.modalTitle')}
          </DialogTitle>
          <div
            className="text-sm text-muted-foreground max-w-lg whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(checkin.greeting) }}
          />
        </div>

        {/* Acorn reward teaser */}
        {checkinReward > 0 && (
          <div className="flex items-center justify-center gap-2 text-sm text-primary font-medium mb-6">
            <AcornIcon size={16} className="text-primary" />
            <span>{t('checkin.rewardMessage', { checkinReward })}</span>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-6">
          {questions.map((question, idx) => (
            <div key={question.id} className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {question.prompt}
                {question.required && <span className="text-red-500 ml-1">*</span>}
              </label>

              {question.type === "scale" && (
                <ScaleInput
                  min={question.scale_min ?? 1}
                  max={question.scale_max ?? 5}
                  minLabel={question.scale_min_label}
                  maxLabel={question.scale_max_label}
                  value={answers[question.id]}
                  onChange={(val) => updateAnswer(question.id, val)}
                />
              )}

              {question.type === "text" && (
                <Textarea
                  rows={3}
                  value={answers[question.id] || ""}
                  onChange={(e) => updateAnswer(question.id, e.target.value)}
                  placeholder={t('checkin.textPlaceholder')}
                />
              )}

              {question.type === "choice" && question.choices && (
                <ChoiceInput
                  choices={question.choices}
                  value={answers[question.id]}
                  onChange={(val) => updateAnswer(question.id, val)}
                />
              )}
            </div>
          ))}
        </div>

        <DialogActions>
          <Button color="secondary" className="rounded-full" onClick={handleClose}>
            {t('checkin.secondaryButton')}
          </Button>
          <Button
            color="primary"
            className="rounded-full"
            disabled={!isFormValid() || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? t('common.submitting') : t('checkin.submitButton')}
          </Button>
        </DialogActions>
      </DialogBody>
    </Dialog>
  );
};

// Scale input — renders radio buttons for each value in range
const ScaleInput: React.FC<{
  min: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
  value?: string;
  onChange: (val: string) => void;
}> = ({ min, max, minLabel, maxLabel, value, onChange }) => {
  const values = [];
  for (let i = min; i <= max; i++) {
    values.push(i);
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-2 flex-wrap">
        {values.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(String(v))}
            className={`w-10 h-10 rounded-full border-2 text-sm font-medium transition-colors ${
              value === String(v)
                ? 'border-primary bg-primary text-white'
                : 'border-border hover:border-primary/50 text-foreground'
            }`}
          >
            {v}
          </button>
        ))}
      </div>
      {(minLabel || maxLabel) && (
        <div className="flex justify-between text-xs text-muted-foreground px-1">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
};

// Choice input — renders radio buttons for each option
const ChoiceInput: React.FC<{
  choices: string[];
  value?: string;
  onChange: (val: string) => void;
}> = ({ choices, value, onChange }) => {
  return (
    <div className="space-y-2">
      {choices.map((choice) => (
        <button
          key={choice}
          type="button"
          onClick={() => onChange(choice)}
          className={`w-full text-left px-4 py-2.5 rounded-lg border-2 text-sm transition-colors ${
            value === choice
              ? 'border-primary bg-primary/5 text-foreground font-medium'
              : 'border-border hover:border-primary/30 text-foreground'
          }`}
        >
          {choice}
        </button>
      ))}
    </div>
  );
};
